import { useNavigate } from 'react-router';

import { toast } from '@/common/components/shared/toaster/toast';
import {
  type ContractType,
  type CreateEmployeeManagerialAccountingInput,
  type EmployeeGender,
  type EmployeeHiringSource,
  type JobHistoryLevel,
  useCreateEmployeeMutation,
} from '@/common/graphql';
import type { EmployeeAddFormDataType } from '@/common/types';
import { ApiUtils, ObjectUtils, StringUtils } from '@/common/utils';

export const useCreateEmployee = () => {
  const [createRecord, { loading }] = useCreateEmployeeMutation();
  const navigate = useNavigate();

  const handleCreateEmployee = async (
    formData: EmployeeAddFormDataType,
    initialManagerialAccounting?: CreateEmployeeManagerialAccountingInput | null,
  ) => {
    await createRecord({
      variables: {
        input: {
          generalInformation: {
            companyEmail: {
              account: formData.companyEmailAccount || '',
              domainId: formData.companyEmailDomain || '',
            },
            dottedLineManagerId: formData.dottedLineManagerId ?? null,
            greenhouseLink: formData.greenhouseLink || null,
            hiredDate: formData.hiredDate,
            hiringSource: formData.hiringSource as EmployeeHiringSource,
            isNewGraduate: formData.isNewGraduate,
            legalFullName: formData.legalFullName || '',
            nameInLocalLanguage: formData.nameInLocalLanguage,
            japanesePhoneticName: formData.japanesePhoneticName,
            nationalityId: formData.nationalityId || '',
            needsProbationReview: formData.needsProbationReview,
            preferredFullName: formData.preferredFullName || '',
            primaryManagerId: formData.primaryManagerId || '',
          },
          personalInformation: {
            bank: {
              accountHolderName: formData.accountHolderName,
              accountNumber: formData.accountNumber,
              branchName: formData.branchName,
              countryId: formData.bankCountryCode,
              name: formData.bankName,
            },
            consultingFeeBank: {
              accountHolderName: formData.consultingFeeAccountHolderName,
              accountNumber: formData.consultingFeeAccountNumber,
              branchName: formData.consultingFeeBranchName,
              countryId: formData.consultingFeeBankCountryCode,
              currencyId: formData.consultingFeeBankCurrencyId,
              name: formData.consultingFeeBankName,
            },
            currentAddress: formData.currentAddress,
            dateOfBirth: formData.dateOfBirth,
            gender: formData.gender as EmployeeGender,
            idExpiryDate: formData.idExpiryDate,
            idIssuedDate: formData.idIssuedDate,
            idIssuedPlace: formData.idIssuedPlace,
            idNumber: formData.idNumber,
            idType: formData.idType,
            maritalStatus: formData.maritalStatus,
            nhiDependents: formData.nhiDependents,
            passportExpiryDate: formData.passportExpiryDate,
            passportIssuedDate: formData.passportIssuedDate,
            passportIssuedPlace: formData.passportIssuedPlace,
            passportName: formData.passportName,
            passportNumber: formData.passportName,
            passportPlaceOfBirth: formData.placeOfBirth,
            pensionVoluntaryContributionRate: formData.pensionVoluntaryContributionRate,
            permanentAddress: formData.permanentAddress,
            personalEmail: formData.personalEmail,
            personalPhoneNumber:
              formData.personalPhoneNumberCountryCode && formData.personalPhoneNumber
                ? {
                    countryId: formData.personalPhoneNumberCountryCode,
                    number: formData.personalPhoneNumber,
                  }
                : null,
            socialInsuranceNotes: formData.socialInsuranceNotes,
            socialInsuranceNumber: formData.socialInsuranceNumber,
            taxCode: formData.taxCode,
            taxWithholdingRate: formData.taxWithholdingRate,
            residentialCountryId: formData.residentialCountryId || '',
            emergencyContacts: formData.emergencyContacts.map(item => ({
              name: item.name,
              relation: item.relation,
              phoneNumber: {
                countryId: item.phoneNumberCountryCode,
                number: item.phoneNumber,
              },
              remarks: item.remarks,
            })),
            visas: formData.visas.map(v => ObjectUtils.deleteFields(v, ['id'])),
            dependents: formData.dependents.map(v => ObjectUtils.deleteFields(v, ['id'])),
            remarks: formData.remarks,
          },
          currentContract: {
            companyId: formData.companyId || '',
            contractType: formData.contractType as ContractType,
            employeeCode: formData.employeeCode?.length ? formData.employeeCode : undefined,
            contractEndDate: StringUtils.emptyToNull(formData.contractEndDate),
          },
          currentJobHistory: {
            contractEndDate: StringUtils.emptyToNull(formData.contractEndDate),
            departmentLead: formData.departmentLead,
            level: formData.level as JobHistoryLevel,
            locationId: formData.locationId || '',
            businessUnitId: formData.businessUnitId || '',
            departmentId: formData.departmentId || '',
            jobId: formData.jobId || '',
            jobTitle: formData.jobTitle || '',
            team: formData.team,
            regionalRole: formData.regionalRole,
          },
          permission: {
            role: formData.role,
            employeeIds: formData.employeeIds,
            companyIds: formData.companyIds,
            idpFullAccess: formData.idpFullAccess,
          },
          externalAccount: {
            autoCreationSettings: {
              enableGoogle: formData.externalAccount.autoCreationSettings.googleWorkspace,
              enableGreenhouse: formData.externalAccount.autoCreationSettings.greenhouse,
            },
            timingToCreate: formData.externalAccount.timingToCreate,
          },
          initialManagerialAccounting: initialManagerialAccounting ?? undefined,
        },
      },
      onCompleted: () => {
        toast.success();
        navigate('/employees');
      },
      onError: error => ApiUtils.handleApiError(error),
    });
  };

  return {
    handleCreateEmployee,
    loading,
  };
};
